import { ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { Asset, Member, Trip } from "../../lib/api.ts";
import { useBackLayer } from "../../lib/backlayer.ts";
import { useThumbs } from "../../lib/thumbs.ts";
import { ICON } from "../../ui/index.ts";
import { describe, MediaCard } from "./MediaCard.tsx";

/* Hallmark · component: modal (media pager) · genre: playful · theme: project palette preserved
 * pre-emit critique: P5 H5 E4 S5 R5 V4
 * states: default · hover · focus · active · disabled · loading · error · success
 * contrast: pass (tokens) · mobile: pass at 320/375/414/768 · tokens: pass
 */

/**
 * The trip's recordings, one card at a time, with the rest waiting either side.
 *
 * Opening a recording opens the whole collection, parked on it. Dragging sideways is the
 * browser's own scrolling with snap points — not a gesture we measure and answer — so the next
 * card is under the thumb as it is pulled rather than appearing once the finger lifts, and a
 * drag somebody changes their mind about falls back where it came from.
 *
 * Every card is the same size, so landing on one never re-measures anything. That is the whole
 * point: what used to lurch was one sheet re-fitting itself around each new picture.
 *
 * The collection is whatever is being looked through — the whole trip in the Media tab, crossing
 * days, or one folder in Tags. Reaching either end asks the owner for more, which is how a day
 * of cards becomes a trip of them without anybody pressing anything.
 */

/** Cards this far from the one being read do their own reading; the rest wait at a thumbnail. */
const AWAKE = 1;

export function MediaPopup({
  trip,
  items,
  startId,
  members,
  onClose,
  onChanged,
  onActive,
  onEdge,
}: {
  trip: Trip;
  /** The collection, in the order the grid is showing it. */
  items: Asset[];
  /** Which of them was opened. */
  startId: string;
  members: Member[];
  onClose: () => void;
  onChanged: () => void;
  /** The card now being read, so the library behind can keep its day strip in step. */
  onActive?: (asset: Asset) => void;
  /** An end is in sight: the owner may extend `items` that way. */
  onEdge?: (towards: -1 | 1) => void;
}) {
  const track = useRef<HTMLDivElement>(null);
  const touched = useRef(false);
  // Back belongs to the thing on screen: it closes the pager and leaves the library standing.
  useBackLayer(onClose);
  // Which recording is being read, by name rather than by position. Cards arriving in front of
  // it shift every index along by one, and an index would have meant the card under the thumb
  // quietly becoming its neighbour for a render — long enough for the library behind to follow
  // the wrong one into the wrong day.
  const [activeId, setActiveId] = useState(startId);
  const at = Math.max(
    0,
    items.findIndex((a) => a.id === activeId),
  );
  const current = items[at] ?? items[0];

  // One batch for the whole collection rather than a grant per card: these are the same
  // thumbnails the grid behind already asked for, and the hook keeps them alive.
  const thumbs = useThumbs(
    trip.id,
    useMemo(() => items.filter((a) => a.thumbReady).map((a) => a.id), [items]),
  );

  /**
   * How far along the track a card sits. A card is narrower than the track — the track is padded
   * so one sits centred in it — so the step is the card's own width, never the track's. Measuring
   * the track put every card past the first further along than the snap point it actually lands
   * on, and the error grows by a card each time: the pager opens a little off centre, and far
   * enough along it steps two cards at once and then reports the one it is not showing.
   */
  const stepOf = (el: HTMLElement) => {
    const card = el.firstElementChild;
    const width = card instanceof HTMLElement ? card.getBoundingClientRect().width : el.clientWidth;
    return width + gapOf(el);
  };

  const offsetOf = (i: number) => {
    const el = track.current;
    return el ? i * stepOf(el) : 0;
  };

  // Park on the recording that was opened, without a slide: the pager should appear already
  // showing it rather than scrolling there in front of the reader.
  useLayoutEffect(() => {
    const el = track.current;
    if (el) el.scrollLeft = offsetOf(at);
    // Only on the way in; every later move is driven by the scroll or by the controls.
  }, []);

  // Cards added in front of the one being read would otherwise slide it sideways, because
  // everything after them moves along by a card's width. Which card is being read does not
  // change — it is named, not numbered — so only the scroll has to be put back.
  const firstId = items[0]?.id;
  const was = useRef<{ firstId: string | undefined }>({ firstId });
  useLayoutEffect(() => {
    const before = was.current.firstId;
    was.current = { firstId };
    if (before === undefined || before === firstId) return;
    const added = items.findIndex((a) => a.id === before);
    if (added <= 0) return;
    const el = track.current;
    if (el) el.scrollLeft += offsetOf(added);
  }, [firstId, items]);

  /** Ask for more as an end comes into view, so the next card is there before it is wanted. */
  useEffect(() => {
    if (!onEdge) return;
    if (at <= 0) onEdge(-1);
    if (at >= items.length - 1) onEdge(1);
  }, [at, items.length, onEdge]);

  useEffect(() => {
    const live = items.find((a) => a.id === activeId);
    if (live) onActive?.(live);
    // The library only cares which recording is being read, not how it came to be this one.
  }, [activeId]);

  /** Which card the track has settled on, read back from where it actually is. */
  function settled() {
    const el = track.current;
    if (!el) return;
    const step = stepOf(el);
    if (step <= 0) return;
    const landed = items[Math.round(el.scrollLeft / step)];
    if (landed && landed.id !== activeId) setActiveId(landed.id);
  }

  function go(delta: -1 | 1) {
    const next = at + delta;
    const card = items[next];
    if (!card) return;
    setActiveId(card.id);
    // The browser's own animation where there is one; jsdom has neither scrolling nor
    // `scrollTo`, and a pager that cannot scroll still has to change cards.
    track.current?.scrollTo?.({ left: offsetOf(next), behavior: "smooth" });
  }

  // Hung on the document rather than wherever it was opened from. The map's own full-screen
  // layer is `position: fixed` with a z-index of its own, which makes it a stacking context —
  // and a sheet opened inside one is sealed in it, however high its own z-index goes. That is
  // how the pager ended up drawn under the app bar and the tab bar on the map.
  return createPortal(
    <div
      className="backdrop pager"
      role="dialog"
      aria-modal="true"
      aria-label={current ? describe(current) : "Recording"}
      onPointerDownCapture={() => (touched.current = true)}
      onClickCapture={(e) => {
        // A tile opens this on pointerup, and the browser then sends the click from that same
        // tap — to a pager that did not exist when the finger went down, landing wherever the
        // tile happened to be. Keyboard activation reports no clicks and is always genuine.
        if (touched.current || e.detail === 0) return;
        e.stopPropagation();
        e.preventDefault();
      }}
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === "Escape") return onClose();
        // Not while somebody is typing a remark: there, the arrows move the caret.
        if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
        if (e.key === "ArrowLeft") go(-1);
        if (e.key === "ArrowRight") go(1);
      }}
    >
      {at > 0 && (
        <button
          type="button"
          className="pager-step back"
          // The backdrop closes on a click, and these sit on it: a press that means "the one
          // before this" must not also mean "put it all away".
          onClick={(e) => {
            e.stopPropagation();
            go(-1);
          }}
          aria-label="Previous recording"
        >
          <ChevronLeft {...ICON} aria-hidden="true" />
        </button>
      )}
      {at < items.length - 1 && (
        <button
          type="button"
          className="pager-step on"
          onClick={(e) => {
            e.stopPropagation();
            go(1);
          }}
          aria-label="Next recording"
        >
          <ChevronRight {...ICON} aria-hidden="true" />
        </button>
      )}

      <div
        className="media-track"
        ref={track}
        onScroll={settled}
        onClick={(e) => e.stopPropagation()}
      >
        {items.map((a, i) => (
          <MediaCard
            key={a.id}
            trip={trip}
            asset={a}
            members={members}
            thumb={thumbs[a.id] ?? null}
            live={Math.abs(i - at) <= AWAKE}
            active={i === at}
            onClose={onClose}
            onChanged={onChanged}
          />
        ))}
      </div>
    </div>,
    document.body,
  );
}

/** The gap between two cards counts towards how far one is from the next. */
function gapOf(el: HTMLElement): number {
  const gap = Number.parseFloat(getComputedStyle(el).columnGap);
  return Number.isFinite(gap) ? gap : 0;
}
